/**
 * M-Pesa two-mode setup: shared types and pure helpers.
 *
 * A location takes M-Pesa either through BizPlus's Daraja account
 * (`bizplus_managed`, "Let BizPlus set it up") or through the owner's own
 * Daraja keys (`merchant_supplied`, "Use my own Daraja keys"). The owner
 * screen is routed from the status object returned by
 * `GET bp_get_mpesa_activation_status_information`; nothing here talks to the
 * network so every branch is unit-testable.
 */

export type MpesaMode = 'bizplus_managed' | 'merchant_supplied';

/**
 * Modes owners can't start yet: the choice still shows, but its Continue
 * button and switch links read "Unavailable". Remove a mode to re-enable it.
 */
const UNAVAILABLE_MPESA_MODES: ReadonlySet<MpesaMode> = new Set<MpesaMode>([
  'merchant_supplied',
]);

export const isMpesaModeAvailable = (mode: MpesaMode): boolean =>
  !UNAVAILABLE_MPESA_MODES.has(mode);

export type MpesaActivationScreen =
  | 'details'
  | 'action_needed'
  | 'on_hold'
  | 'pending'
  | 'enter_code'
  | 'setting_up'
  | 'live'
  | 'suspended'
  | 'revoked';

export type MpesaProfileStatus =
  | 'not_started'
  | 'draft'
  | 'needs_bank_settlement'
  | 'needs_org_portal'
  | 'awaiting_session'
  | 'awaiting_otp'
  | 'awaiting_credentials'
  | 'awaiting_passkey'
  | 'verifying'
  | 'active'
  | 'suspended'
  | 'revoked';

export type MpesaSessionStatus =
  | 'waiting_for_ops'
  | 'ops_ready'
  | 'form_submitted'
  | 'otp_received'
  | 'completed'
  | 'expired'
  | 'cancelled';

export type MpesaC2bUrlsState =
  | 'unregistered'
  | 'bizplus_legacy'
  | 'bizplus_tokenized'
  | 'foreign';

export type MpesaHealth = 'ok' | 'oauth_failing';

export type MpesaShortcodeType = 'till' | 'paybill';

export type MpesaChecklistStep =
  | 'details'
  | 'consent'
  | 'session'
  | 'go_live'
  | 'keys'
  | 'verified';

export interface MpesaActivationChecklistItem {
  step: MpesaChecklistStep;
  done: boolean;
  current_session_status?: MpesaSessionStatus | null;
}

export interface MpesaActivationSession {
  _id: string;
  bp_status: MpesaSessionStatus;
  bp_channel?: 'phone_call' | 'in_app';
  bp_requested_slot_at?: string | null;
  bp_expires_at?: string | null;
}

/** Owner-facing status object. Never carries secrets. */
export interface MpesaActivationStatus {
  _id?: string;
  bp_mode: MpesaMode | null;
  bp_status: MpesaProfileStatus;
  bp_screen: MpesaActivationScreen;
  bp_blocked_reason?: 'OTHER_INTEGRATION' | null;
  bp_shortcode_type?: MpesaShortcodeType | null;
  bp_c2b_shortcode?: string | null;
  bp_stk_business_shortcode?: string | null;
  bp_till_number?: string | null;
  bp_organization_name?: string | null;
  bp_otp_phone_ending?: string | null;
  bp_consent_accepted?: boolean;
  /** Current authorisation version; must be echoed back on submit. */
  bp_terms_version?: string | null;
  /** Current authorisation text, served by the backend. */
  bp_terms_text?: string | null;
  bp_health?: MpesaHealth | null;
  bp_c2b_urls_state?: MpesaC2bUrlsState | null;
  bp_last_error_code?: string | null;
  bp_last_verified_at?: string | null;
  bp_checklist: MpesaActivationChecklistItem[];
  bp_session: MpesaActivationSession | null;
}

export type MpesaActivationStreamStatus =
  | 'ops_ready'
  | 'form_submitted'
  | 'code_requested'
  | 'otp_received'
  | 'go_live_done'
  | 'waiting_for_ops'
  | 'active'
  | 'verification_failed';

export interface MpesaActivationStreamEvent {
  sessionId: string;
  configId?: string;
  status: MpesaActivationStreamStatus;
  message?: string;
}

/** What the M-Pesa screen renders. One value per distinct screen. */
export type MpesaSetupView =
  | 'choose_mode'
  | 'managed_details'
  | 'managed_action_needed'
  | 'managed_on_hold'
  | 'managed_pending'
  | 'managed_enter_code'
  | 'managed_setting_up'
  | 'managed_live'
  | 'managed_suspended'
  | 'managed_revoked'
  | 'own_keys_draft'
  | 'own_keys_revoked'
  | 'own_keys_live';

const MANAGED_SCREEN_TO_VIEW: Record<MpesaActivationScreen, MpesaSetupView> = {
  details: 'managed_details',
  action_needed: 'managed_action_needed',
  on_hold: 'managed_on_hold',
  pending: 'managed_pending',
  enter_code: 'managed_enter_code',
  setting_up: 'managed_setting_up',
  live: 'managed_live',
  suspended: 'managed_suspended',
  revoked: 'managed_revoked',
};

/**
 * Routes the status object to a view. `null` means "no status yet" (loading).
 * Own keys distinguishes a draft (keys still missing after a mode switch) and
 * a setup BizPlus disconnected (keys deleted) from everything else; the live
 * card shows `bp_status` itself.
 */
export function resolveMpesaSetupView(
  status: MpesaActivationStatus | null | undefined,
): MpesaSetupView | null {
  if (!status) return null;
  if (status.bp_mode === 'merchant_supplied') {
    if (status.bp_status === 'draft') return 'own_keys_draft';
    if (status.bp_status === 'revoked') return 'own_keys_revoked';
    return 'own_keys_live';
  }
  if (status.bp_mode === 'bizplus_managed') {
    return MANAGED_SCREEN_TO_VIEW[status.bp_screen] ?? 'managed_details';
  }
  return 'choose_mode';
}

const STREAM_SCREENS: ReadonlyArray<MpesaActivationScreen> = [
  'pending',
  'enter_code',
  'setting_up',
];

/** The activation stream is only useful while BizPlus is working on it. */
export function shouldOpenActivationStream(
  status: MpesaActivationStatus | null | undefined,
): boolean {
  return Boolean(
    status &&
      status.bp_mode === 'bizplus_managed' &&
      status.bp_session?._id &&
      STREAM_SCREENS.includes(status.bp_screen),
  );
}

export const MPESA_STATUS_POLL_MS = 20_000;

const POLL_SCREENS: ReadonlyArray<MpesaActivationScreen> = [
  'pending',
  'enter_code',
];

/**
 * Safety net while the owner waits for BizPlus: reload the status on a timer
 * so a missed stream event delays the screen instead of leaving it stuck.
 */
export function shouldPollActivationStatus(
  status: MpesaActivationStatus | null | undefined,
): boolean {
  return Boolean(
    status &&
      status.bp_mode === 'bizplus_managed' &&
      POLL_SCREENS.includes(status.bp_screen),
  );
}

/** The progress checklist is shown only while BizPlus is working on it. */
export function shouldShowActivationChecklist(
  status: MpesaActivationStatus | null | undefined,
): boolean {
  return Boolean(
    status &&
      status.bp_mode === 'bizplus_managed' &&
      STREAM_SCREENS.includes(status.bp_screen),
  );
}

export const MPESA_CHECKLIST_ORDER: ReadonlyArray<MpesaChecklistStep> = [
  'details',
  'consent',
  'session',
  'go_live',
  'keys',
  'verified',
];

export const MPESA_CHECKLIST_COPY: Record<
  MpesaChecklistStep,
  { label: string; description: string }
> = {
  details: {
    label: 'Business details',
    description: 'Your Till or Paybill and business name',
  },
  consent: {
    label: 'Authorisation',
    description: 'You allow BizPlus to set up M-Pesa for you',
  },
  session: {
    label: 'Setup call with BizPlus',
    description: 'BizPlus calls you to do the Safaricom steps',
  },
  go_live: {
    label: 'Safaricom approval',
    description: 'Safaricom approves your number for BizPlus',
  },
  keys: {
    label: 'Connecting your number',
    description: 'BizPlus links your number to BizPlus',
  },
  verified: {
    label: 'Test payment',
    description: 'BizPlus makes a KES 1 test payment, Keep your phone nearby',
  },
};

/** Session-step description that follows the live session status. */
export function describeSessionStep(
  sessionStatus: MpesaSessionStatus | null | undefined,
): string {
  switch (sessionStatus) {
    case 'waiting_for_ops':
      return 'Waiting for a BizPlus agent';
    case 'ops_ready':
      return 'A BizPlus agent is working on it';
    case 'form_submitted':
      return 'Safaricom is sending the code';
    case 'otp_received':
      return 'BizPlus has your code';
    default:
      return MPESA_CHECKLIST_COPY.session.description;
  }
}

/**
 * App copy for error codes where the API doc asks for specific wording.
 * Anything else shows the server's `message`.
 */
export const MPESA_ACTIVATION_ERROR_COPY: Record<string, string> = {
  INVALID_OTP: 'Enter the code exactly as it appears in the SMS.',
  OPS_NOT_CONNECTED:
    'BizPlus isn’t connected right now. Try again in a moment.',
  TOO_MANY_ATTEMPTS: 'Too many wrong tries. BizPlus will send a new code.',
  TOO_MANY_CODE_REQUESTS:
    'You’ve asked for 3 new codes. BizPlus will call you.',
  OFFBOARDING_PENDING:
    'BizPlus is still removing the previous setup. Try again later.',
  TERMS_VERSION_MISMATCH:
    'The authorisation text has changed. Read and accept the current version.',
};

/** Error codes after which the app should simply reload the status object. */
export const MPESA_ACTIVATION_RELOAD_CODES: ReadonlySet<string> = new Set([
  'ACTIVATION_IN_PROGRESS',
  'SESSION_ALREADY_OPEN',
  'WRONG_STATE',
  'SESSION_NOT_FOUND',
  'USE_REVOKE',
  'SAME_MODE',
  'NOT_MANAGED',
]);

export function getActivationErrorCopy(
  code: string | undefined,
  serverMessage: string,
): string {
  if (code && MPESA_ACTIVATION_ERROR_COPY[code]) {
    return MPESA_ACTIVATION_ERROR_COPY[code];
  }
  return serverMessage;
}

// ---------------------------------------------------------------------------
// "Let BizPlus set it up" details form
// ---------------------------------------------------------------------------

export type MpesaSettlementType = 'bank' | 'mpesa';
export type MpesaOtherIntegration = 'none' | 'yes' | 'unknown';

export interface MpesaActivationDetailsDraft {
  bp_shortcode_type: MpesaShortcodeType | '';
  bp_paybill_number: string;
  bp_till_number: string;
  bp_store_or_ho_number: string;
  bp_settlement_type: MpesaSettlementType | '';
  bp_organization_name: string;
  /** null until the owner answers Yes/No. */
  bp_has_org_portal_user: boolean | null;
  bp_org_portal_username: string;
  bp_otp_phone_ending: string;
  bp_other_integration: MpesaOtherIntegration | '';
  bp_signer_name: string;
  bp_signer_role: string;
}

export const createInitialMpesaActivationDetailsDraft =
  (): MpesaActivationDetailsDraft => ({
    // Till pre-selected so a new owner starts on the common case
    bp_shortcode_type: 'till',
    bp_paybill_number: '',
    bp_till_number: '',
    bp_store_or_ho_number: '',
    bp_settlement_type: '',
    bp_organization_name: '',
    bp_has_org_portal_user: null,
    bp_org_portal_username: '',
    bp_otp_phone_ending: '',
    bp_other_integration: '',
    bp_signer_name: '',
    bp_signer_role: '',
  });

export type MpesaActivationDetailsErrors = Partial<
  Record<keyof MpesaActivationDetailsDraft, string>
>;

const SHORTCODE_RE = /^\d{5,10}$/;
const PHONE_ENDING_RE = /^\d{3}$/;

export const MPESA_ORGANIZATION_NAME_MAX = 100;
export const MPESA_ORG_PORTAL_USERNAME_MAX = 60;
export const MPESA_SIGNER_NAME_MAX = 100;
export const MPESA_SIGNER_ROLE_MAX = 60;

/** Mirrors the backend rules for `POST bp_start_mpesa_activation_information`. */
export function validateMpesaActivationDetails(
  draft: MpesaActivationDetailsDraft,
): MpesaActivationDetailsErrors {
  const errors: MpesaActivationDetailsErrors = {};

  if (
    draft.bp_shortcode_type !== 'till' &&
    draft.bp_shortcode_type !== 'paybill'
  ) {
    errors.bp_shortcode_type = 'Choose Till Number or Paybill.';
  } else if (draft.bp_shortcode_type === 'paybill') {
    if (!SHORTCODE_RE.test(draft.bp_paybill_number.trim())) {
      errors.bp_paybill_number = 'Enter 5 to 10 digits.';
    }
  } else {
    if (!SHORTCODE_RE.test(draft.bp_till_number.trim())) {
      errors.bp_till_number = 'Enter 5 to 10 digits.';
    }
    if (!SHORTCODE_RE.test(draft.bp_store_or_ho_number.trim())) {
      errors.bp_store_or_ho_number = 'Enter 5 to 10 digits.';
    }
    if (
      draft.bp_settlement_type !== 'bank' &&
      draft.bp_settlement_type !== 'mpesa'
    ) {
      errors.bp_settlement_type = 'Choose where the Till’s money is settled.';
    }
  }

  const organizationName = draft.bp_organization_name.trim();
  if (!organizationName) {
    errors.bp_organization_name =
      'Enter the business name registered with M-PESA.';
  } else if (organizationName.length > MPESA_ORGANIZATION_NAME_MAX) {
    errors.bp_organization_name = `Use at most ${MPESA_ORGANIZATION_NAME_MAX} characters.`;
  }

  if (draft.bp_has_org_portal_user === null) {
    errors.bp_has_org_portal_user =
      'Tell us whether you have an Org Portal user.';
  } else if (draft.bp_has_org_portal_user) {
    const username = draft.bp_org_portal_username.trim();
    if (!username) {
      errors.bp_org_portal_username = 'Enter the Org Portal username.';
    } else if (username.length > MPESA_ORG_PORTAL_USERNAME_MAX) {
      errors.bp_org_portal_username = `Use at most ${MPESA_ORG_PORTAL_USERNAME_MAX} characters.`;
    }
    if (!PHONE_ENDING_RE.test(draft.bp_otp_phone_ending.trim())) {
      errors.bp_otp_phone_ending = 'Enter the last 3 digits of the phone.';
    }
  }

  if (
    draft.bp_other_integration !== 'none' &&
    draft.bp_other_integration !== 'yes' &&
    draft.bp_other_integration !== 'unknown'
  ) {
    errors.bp_other_integration =
      'Tell us whether another system uses this number.';
  }

  return errors;
}

/** Signer fields live on the authorise step; validated separately. */
export function validateMpesaActivationSigner(
  draft: Pick<MpesaActivationDetailsDraft, 'bp_signer_name' | 'bp_signer_role'>,
): MpesaActivationDetailsErrors {
  const errors: MpesaActivationDetailsErrors = {};
  const signerName = draft.bp_signer_name.trim();
  if (!signerName) {
    errors.bp_signer_name = 'Enter your full name.';
  } else if (signerName.length > MPESA_SIGNER_NAME_MAX) {
    errors.bp_signer_name = `Use at most ${MPESA_SIGNER_NAME_MAX} characters.`;
  }
  if (draft.bp_signer_role.trim().length > MPESA_SIGNER_ROLE_MAX) {
    errors.bp_signer_role = `Use at most ${MPESA_SIGNER_ROLE_MAX} characters.`;
  }
  return errors;
}

export interface StartMpesaActivationPayload {
  bp_shortcode_type: MpesaShortcodeType;
  bp_paybill_number?: string;
  bp_till_number?: string;
  bp_store_or_ho_number?: string;
  bp_settlement_type?: MpesaSettlementType;
  bp_organization_name: string;
  bp_has_org_portal_user: boolean;
  bp_org_portal_username?: string;
  bp_otp_phone_ending?: string;
  bp_other_integration: MpesaOtherIntegration;
  bp_accept_terms: true;
  bp_signer_name: string;
  bp_signer_role?: string;
  bp_terms_version: string;
}

/**
 * Builds the submit body. Callers validate first; this throws if the draft is
 * not submittable so a broken call site fails loudly instead of sending junk.
 */
export function buildStartMpesaActivationPayload(
  draft: MpesaActivationDetailsDraft,
  termsVersion: string,
): StartMpesaActivationPayload {
  const detailErrors = validateMpesaActivationDetails(draft);
  const signerErrors = validateMpesaActivationSigner(draft);
  if (Object.keys(detailErrors).length || Object.keys(signerErrors).length) {
    throw new Error('M-Pesa activation details are incomplete');
  }
  if (!termsVersion) {
    throw new Error('M-Pesa authorisation version is missing');
  }

  const shortcodeType = draft.bp_shortcode_type as MpesaShortcodeType;
  const hasPortalUser = draft.bp_has_org_portal_user === true;
  const signerRole = draft.bp_signer_role.trim();

  const payload: StartMpesaActivationPayload = {
    bp_shortcode_type: shortcodeType,
    bp_organization_name: draft.bp_organization_name.trim(),
    bp_has_org_portal_user: hasPortalUser,
    bp_other_integration: draft.bp_other_integration as MpesaOtherIntegration,
    bp_accept_terms: true,
    bp_signer_name: draft.bp_signer_name.trim(),
    bp_terms_version: termsVersion,
  };

  if (shortcodeType === 'paybill') {
    payload.bp_paybill_number = draft.bp_paybill_number.trim();
  } else {
    payload.bp_till_number = draft.bp_till_number.trim();
    payload.bp_store_or_ho_number = draft.bp_store_or_ho_number.trim();
    payload.bp_settlement_type =
      draft.bp_settlement_type as MpesaSettlementType;
  }

  if (hasPortalUser) {
    payload.bp_org_portal_username = draft.bp_org_portal_username.trim();
    payload.bp_otp_phone_ending = draft.bp_otp_phone_ending.trim();
  }

  if (signerRole) {
    payload.bp_signer_role = signerRole;
  }

  return payload;
}

/**
 * Prefills the details form from a status object (resubmit after
 * `action_needed`, or start again after `revoked`). Fields the server does not
 * echo back (portal username, settlement, other integration, signer) stay as
 * they are in the draft.
 */
export function mergeDraftFromActivationStatus(
  draft: MpesaActivationDetailsDraft,
  status: MpesaActivationStatus | null | undefined,
): MpesaActivationDetailsDraft {
  if (!status || status.bp_mode !== 'bizplus_managed') return draft;
  const next: MpesaActivationDetailsDraft = { ...draft };
  if (
    status.bp_shortcode_type === 'till' ||
    status.bp_shortcode_type === 'paybill'
  ) {
    next.bp_shortcode_type = status.bp_shortcode_type;
  }
  if (status.bp_shortcode_type === 'paybill' && status.bp_c2b_shortcode) {
    next.bp_paybill_number = status.bp_c2b_shortcode;
  }
  if (status.bp_shortcode_type === 'till') {
    if (status.bp_till_number || status.bp_c2b_shortcode) {
      next.bp_till_number = String(
        status.bp_till_number || status.bp_c2b_shortcode,
      );
    }
    if (status.bp_stk_business_shortcode) {
      next.bp_store_or_ho_number = status.bp_stk_business_shortcode;
    }
  }
  if (status.bp_organization_name) {
    next.bp_organization_name = status.bp_organization_name;
  }
  if (status.bp_otp_phone_ending) {
    next.bp_otp_phone_ending = status.bp_otp_phone_ending;
    next.bp_has_org_portal_user = true;
  }
  return next;
}

// ---------------------------------------------------------------------------
// SMS code
// ---------------------------------------------------------------------------

export const MPESA_OTP_MIN_LENGTH = 4;
export const MPESA_OTP_MAX_LENGTH = 8;

export function isValidMpesaActivationOtp(code: string): boolean {
  const digits = code.trim();
  return (
    /^\d+$/.test(digits) &&
    digits.length >= MPESA_OTP_MIN_LENGTH &&
    digits.length <= MPESA_OTP_MAX_LENGTH
  );
}

// ---------------------------------------------------------------------------
// Own keys helpers
// ---------------------------------------------------------------------------

/** Payment links are registered only in this state. */
export function arePaymentLinksRegistered(
  state: MpesaC2bUrlsState | null | undefined,
): boolean {
  return state === 'bizplus_tokenized';
}

export function describePaymentLinksState(
  state: MpesaC2bUrlsState | null | undefined,
): { label: string; tone: 'success' | 'due' | 'error' | 'neutral' } {
  switch (state) {
    case 'bizplus_tokenized':
      return { label: 'Registered', tone: 'success' };
    case 'bizplus_legacy':
      return { label: 'Needs re-registering', tone: 'due' };
    case 'foreign':
      return { label: 'Used by another system', tone: 'error' };
    case 'unregistered':
      return { label: 'Not registered', tone: 'due' };
    default:
      return { label: 'Unknown', tone: 'neutral' };
  }
}

/** "Till 123456" / "Paybill 400200" / "Shortcode 123"; null without a number. */
export function formatMpesaNumberLabel(
  shortcodeType: string | null | undefined,
  shortcode: string | null | undefined,
): string | null {
  const number = String(shortcode || '').trim();
  if (!number) return null;
  const type = String(shortcodeType || '').toLowerCase();
  const prefix =
    type === 'till' ? 'Till' : type === 'paybill' ? 'Paybill' : 'Shortcode';
  return `${prefix} ${number}`;
}

export function describeVerifyStep(
  step: 'oauth' | 'c2b' | 'test_prompt' | string | null | undefined,
): string {
  switch (step) {
    case 'oauth':
      return 'Keys';
    case 'c2b':
      return 'Payment links';
    case 'test_prompt':
      return 'Test prompt';
    default:
      return 'Check';
  }
}

export const MPESA_STATUS_LABELS: Record<MpesaProfileStatus, string> = {
  not_started: 'Not connected',
  draft: 'Not connected',
  needs_bank_settlement: 'Action needed',
  needs_org_portal: 'Action needed',
  awaiting_session: 'Submitted',
  awaiting_otp: 'Code needed',
  awaiting_credentials: 'Setting up',
  awaiting_passkey: 'Setting up',
  verifying: 'Setting up',
  active: 'Active',
  suspended: 'Paused',
  revoked: 'Stopped',
};
